import { useState } from 'react'
import { UseFormSetError } from 'react-hook-form'

import { TFunction } from 'i18next'

import { useEventCapture } from '@surglogs-app/analytics'
import { useHandler } from '@surglogs-app/hooks'
import {
  checkErrorCode,
  useAuthMfaSetupVerifyPostMutation,
} from '@surglogs-app/network'
import { useToast } from '@surglogs-app/ui.toast'

interface UseMfaSetupVerifySubmitHandlerProps {
  transactionId: string
  onClose: () => void
  setError: UseFormSetError<{ mfaCode: string }>
  onMfaSetupCompleted: () => Promise<void>
  t: TFunction<any>
}

export const useMfaSetupVerifySubmitHandler = ({
  transactionId,
  onClose,
  setError,
  onMfaSetupCompleted,
  t,
}: UseMfaSetupVerifySubmitHandlerProps) => {
  const { captureEvent } = useEventCapture()
  const { showToast, showErrorToast } = useToast()

  const [isProcessingVerification, setIsProcessingVerification] =
    useState(false)

  const { mutateAsync } = useAuthMfaSetupVerifyPostMutation({
    opts: { throwOnError: false },
  })

  const onSubmit = useHandler(async ({ mfaCode }: { mfaCode: string }) => {
    try {
      setIsProcessingVerification(true)
      await mutateAsync({
        mfa_code: mfaCode,
        transaction_id: transactionId,
      })

      captureEvent('mfa_setup_code_submitted', {
        context: 'account',
        success: true,
      })

      await onMfaSetupCompleted()
      onClose()
      showToast({
        title: t('mfaCodeVerify.success.title'),
        text: t('mfaCodeVerify.success.text'),
      })
    } catch (error) {
      captureEvent('mfa_setup_code_submitted', {
        context: 'account',
        success: false,
      })

      if (checkErrorCode(error, 'authentication_error')) {
        setError('mfaCode', {
          type: 'validate',
          message: t('mfaCodeVerify.invalidCode'),
        })
      } else {
        showErrorToast({ error })
      }
    } finally {
      setIsProcessingVerification(false)
    }
  })

  return { onSubmit, isPending: isProcessingVerification }
}
